/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import { isMimeType } from '@lexical/utils';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $insertNodes,
  $isRootNode,
  COMMAND_PRIORITY_LOW,
  NodeKey,
} from 'lexical';
import { useEffect } from 'react';
import { uploadImage } from '@/components/file-thumbnail/utils';
import UploadImageNode, {
  $createUploadImageNode,
} from '@/components/LexicalEditor/nodes/UploadImageNode';
import { useSnackbar } from '@/hooks/global-tools';
import { validateImageSize } from '../../utils/validateImageSize';
import {
  $getSelectedListItem,
  $insertImageInListItem,
} from '../../utils/listItemImage';
import { useTranslation } from 'react-i18next';

const ACCEPTABLE_IMAGE_TYPES = [
  'image/',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/webp',
];

export default function DragDropPaste({
  inline = false,
}: { inline?: boolean } = {}): null {
  const [editor] = useLexicalComposerContext();
  const toast = useSnackbar();
  const { t } = useTranslation();

  useEffect(() => {
    return editor.registerCommand(
      DRAG_DROP_PASTE,
      (files: File[]) => {
        (async () => {
          for (const file of files) {
            if (!isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) continue;

            // 이미지 크기 검증
            const validation = await validateImageSize(file);
            if (!validation.valid) {
              toast.show({
                severity: 'error',
                message: t('이미지는 최대 3000px*6000px까지 첨부 가능합니다.'),
              });
              continue;
            }

            if (file.size >= 100000000) {
              toast.show({
                severity: 'info',
                message: t(
                  '용량이 큰 이미지는 불러오는 데 시간이 걸릴 수 있어요. 잠시만 기다려 주세요.',
                ),
                duration: 5000,
              });
            }

            let nodeKey: NodeKey;
            const objectUrl = URL.createObjectURL(file);

            editor.update(() => {
              // 1. 붙여넣자마자 로딩 노드 추가
              const listItem = inline ? null : $getSelectedListItem();
              const node = $createUploadImageNode({
                src: objectUrl,
                altText: file.name,
                status: 'loading',
                inline: inline || listItem !== null,
              });
              if (listItem) {
                $insertImageInListItem(listItem, node);
              } else {
                $insertNodes([node]);
              }
              nodeKey = node.getKey();

              // 2. 이미지 뒤 커서 이동 — inline(품의 코멘트)은 줄바꿈 없이 같은 줄 유지
              const cursor = $createTextNode('');
              if (!inline && !listItem) {
                if ($isRootNode(node.getParent())) {
                  const p = $createParagraphNode();
                  p.append(cursor);
                  node.insertAfter(p);
                } else {
                  const br = $createLineBreakNode();
                  node.insertAfter(br);
                  br.insertAfter(cursor);
                }
              } else {
                node.insertAfter(cursor);
              }
              cursor.select();
            });

            // 3. 업로드 실행
            uploadImage(file)
              .then((uploaded) => {
                const serverUrl = uploaded.url;
                const img = new Image();

                img.onload = () => {
                  editor.update(() => {
                    const node = $getNodeByKey(
                      nodeKey,
                    ) as UploadImageNode | null;
                    if (node) {
                      node.setSrc(serverUrl);
                      node.setStatus('success');
                    }
                  });
                  URL.revokeObjectURL(objectUrl);
                };
                img.onerror = () => {
                  editor.update(() => {
                    const node = $getNodeByKey(
                      nodeKey,
                    ) as UploadImageNode | null;
                    node?.setStatus('error');
                  });
                  URL.revokeObjectURL(objectUrl);
                };
                img.src = serverUrl;
              })
              .catch(() => {
                editor.update(() => {
                  const node = $getNodeByKey(nodeKey) as UploadImageNode | null;
                  node?.setStatus('error');
                });
              })
              .finally(() => {
                URL.revokeObjectURL(objectUrl);
              });
          }
        })();

        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, toast, t, inline]);
  return null;
}
